import { SettingsPaths } from "@sage/sage-network-shared-ui/shared-constants";
import { ComponentType, lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";

import { loggerClient } from "@utils/logger";

import FullScreenLoader from "@components/FullScreenLoader";
import Page404 from "@components/Page404";
import { appRoutes } from "@constants/routing.constants";
import BaseLayout from "@container/BaseLayout";
import EmbedLayout from "@container/EmbedLayout";
import { I18nSetter } from "@/i18n";
import { FallbackLanguage, FeatureNamespaces } from "@/i18n/i18nConstants";

// The e-invoicing-identifiers root path maps to the same hierarchy used internally
const EINV_ID_BASE = `${appRoutes.eInvoicingIdentifiers}` as const;

/**
 * Lazy load with retry logic
 * Mirrors sage-network's lazyRetry pattern
 * On chunk load failure, refreshes once before showing error
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyRetry = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentImport: () => Promise<{ default: ComponentType<any> }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ default: ComponentType<any> }> => {
  return new Promise((resolve) => {
    const hasRefreshed = JSON.parse(
      globalThis.sessionStorage.getItem("retry-lazy-refreshed") || "false"
    );

    componentImport()
      .then((component) => {
        globalThis.sessionStorage.setItem("retry-lazy-refreshed", "false");
        resolve(component);
      })
      .catch((error: unknown) => {
        loggerClient.error(
          "Error loading component",
          error instanceof Error ? error : new Error(String(error))
        );
        if (!hasRefreshed) {
          globalThis.sessionStorage.setItem("retry-lazy-refreshed", "true");
          globalThis.location.reload();
          return;
        }

        resolve({
          default: () => <Page404 />
        });
      });
  });
};

// ─── Bootstrap / Flow Pages ──────────────────────────────────────
// eslint-disable-next-line import/no-cycle
const BootstrapPage = lazy(() =>
  lazyRetry(() => import("@/pages/BootstrapPage"))
);
const FlowExpiredPage = lazy(() =>
  lazyRetry(() => import("@/pages/FlowExpiredPage"))
);
const FlowErrorPage = lazy(() =>
  lazyRetry(() => import("@/pages/FlowErrorPage"))
);
const FlowCompletedPage = lazy(() =>
  lazyRetry(() => import("@/pages/FlowCompletedPage"))
);
const SessionExpiredPage = lazy(() =>
  lazyRetry(() => import("@/pages/SessionExpiredPage"))
);

// ─── Feature Module Pages ────────────────────────────────────────
const HomePage = lazy(() => lazyRetry(() => import("@features/Home/HomePage")));
const ReceivedInvoices = lazy(() =>
  lazyRetry(() => import("@features/ReceivedInvoices/Component"))
);

// ─── E-Invoicing Identifiers ────────────────────────────────────
const EInvoicingIdentifiersLayout = lazy(() =>
  lazyRetry(() => import("@features/EInvoicingIdentifiers/tsx/Layout"))
);
const EInvoicingIdentifiersContainer = lazy(() =>
  lazyRetry(() => import("@features/EInvoicingIdentifiers/tsx/Container"))
);
const EInvoicingIdentifiersRegistration = lazy(() =>
  lazyRetry(
    () =>
      import("@features/EInvoicingIdentifiers/modules/IdentifierRegistration")
  )
);
const EInvoicingIdentifierRegistrationDetail = lazy(() =>
  lazyRetry(
    () =>
      import("@features/EInvoicingIdentifiers/modules/IdentifierRegistrationDetail")
  )
);

const withSuspense = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.LazyExoticComponent<ComponentType<any>>,
  props?: Record<string, unknown>
) => (
  <Suspense fallback={<FullScreenLoader />}>
    <Component {...props} />
  </Suspense>
);

/**
 * Wraps a lazy feature component with Suspense and I18nSetter.
 * I18nSetter triggers loadNamespaces; i18next's Suspense integration
 * holds the render until the namespace is available — the outer Suspense
 * shows the loader for both the lazy chunk and the translations.
 */
const withFeature = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.LazyExoticComponent<ComponentType<any>>,
  featureNamespaces: string[],
  props?: Record<string, unknown>
) => (
  <Suspense fallback={<FullScreenLoader />}>
    <I18nSetter
      LocalMFEName="Feature"
      noSuspense
      activeNamespaces={{ Feature: featureNamespaces }}
      fallbackLanguage={FallbackLanguage}
    >
      <Component {...props} />
    </I18nSetter>
  </Suspense>
);

/** E-Invoicing Identifiers sub-routes */
const identifierChildRoutes = (): RouteObject[] => [
  {
    index: true,
    element: withFeature(EInvoicingIdentifiersContainer, [
      FeatureNamespaces.eInvoicingIdentifiers
    ])
  },
  {
    path: SettingsPaths.ELECTRONIC_INVOICING_SETTINGS_IDENTIFIERS_LIST, // :accessPointId
    element: withFeature(
      EInvoicingIdentifiersContainer,
      [FeatureNamespaces.eInvoicingIdentifiers],
      { showlist: true }
    )
  },
  {
    path: `${SettingsPaths.ELECTRONIC_INVOICING_SETTINGS_IDENTIFIERS_LIST}/${SettingsPaths.ELECTRONIC_INVOICING_SETTINGS_IDENTIFIERS_REGISTER}`,
    element: withFeature(EInvoicingIdentifiersRegistration, [
      FeatureNamespaces.eInvoicingIdentifiers
    ])
  },
  {
    path: `${SettingsPaths.ELECTRONIC_INVOICING_SETTINGS_IDENTIFIERS_LIST}/${SettingsPaths.ELECTRONIC_INVOICING_SETTINGS_REGISTRATION_STATUS}`,
    element: withFeature(EInvoicingIdentifierRegistrationDetail, [
      FeatureNamespaces.eInvoicingIdentifiers
    ])
  }
];

// Unused — kept for type reference only
// eslint-disable-next-line sonarjs/void-use
void EINV_ID_BASE;

const routes: RouteObject[] = [
  // ── Bootstrap entry points ───────────────────────────────────────
  {
    path: appRoutes.bootstrap,
    element: withSuspense(BootstrapPage)
  },
  {
    path: appRoutes.launch,
    element: withSuspense(BootstrapPage)
  },

  // ── Flow routes (embedded mode — minimal chrome) ────────────────
  {
    path: "/flows",
    element: <EmbedLayout />,
    children: [
      {
        path: "einvoice-onboarding",
        element: withSuspense(EInvoicingIdentifiersLayout),
        children: identifierChildRoutes()
      },
      {
        path: "einvoice-received-invoices",
        element: withFeature(ReceivedInvoices, [
          FeatureNamespaces.receivedInvoices
        ])
      }
    ]
  },

  // ── Status / error pages (usable in both modes) ────────────────
  {
    path: appRoutes.expired,
    element: withSuspense(FlowExpiredPage)
  },
  {
    path: appRoutes.error,
    element: withSuspense(FlowErrorPage)
  },
  {
    path: "/flow-completed",
    element: withSuspense(FlowCompletedPage)
  },
  {
    path: "/session-expired",
    element: withSuspense(SessionExpiredPage)
  },

  // ── Standalone routes (full chrome — Header + Sidebar) ─────────
  {
    path: "/",
    element: <BaseLayout />,
    children: [
      {
        path: appRoutes.home,
        element: withSuspense(HomePage)
      },
      {
        path: appRoutes.invoices,
        element: withFeature(ReceivedInvoices, [
          FeatureNamespaces.receivedInvoices
        ])
      },

      // ── E-Invoicing Identifiers (address onboarding) ─────────────
      {
        path: appRoutes.eInvoicingIdentifiers,
        children: [...identifierChildRoutes()]
      }
    ]
  },

  // ── Catch-all ───────────────────────────────────────────────────
  {
    path: "*",
    element: <Navigate to="/" replace />
  }
];

export const router = createBrowserRouter(routes);

export default routes;
